import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { RetryableError } from '../../../shared/RetryableError';
import { destinationPath, reasonInfo, type ExceptionDestination, type TaxSetupExceptions } from '../../tax-settings/exceptions';
import { peopleGateway } from '../index';
import '../../tax-settings/tax-settings.css';

const PAGE_SIZE = 25;

function linkLabel(destination: ExceptionDestination): string {
  if (destination.step === 'taxes-exemptions') return 'Open Tax Settings';
  return destination.section === 'location' ? 'Open Work Location' : 'Open Home Address';
}

/**
 * People > Tax setup exceptions. The company-wide to-do list behind each employee's Tax Settings: who has a tax setup item to look at, why, and where
 * to fix it. Read-only and informational: it lists employee NUMBERS only, blocks nothing, and an employee who is not listed is not thereby ready for
 * payroll (EPN has not certified any jurisdiction yet). The reason chips narrow the list; their counts always cover the whole company.
 */
export function TaxSetupExceptionsPage() {
  const [reason, setReason] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  // What was read (or why it failed) is stored with the request it answers, so a result for an earlier page or reason is never shown for the current one,
  // and "loading" is simply "nothing stored for this request yet".
  const requestKey = `${page}|${reason ?? ''}|${attempt}`;
  const [result, setResult] = useState<{ key: string; report: TaxSetupExceptions } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  // The summary counts do not change when the list narrows, so the chips keep the latest read (they never disappear while a narrowed list loads).
  const [chips, setChips] = useState<TaxSetupExceptions['summary'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await peopleGateway.listTaxSetupExceptions({ page, pageSize: PAGE_SIZE, ...(reason ? { reason } : {}) });
        if (cancelled) return;
        setResult({ key: requestKey, report: next });
        setChips(next.summary);
      } catch (err) {
        if (!cancelled) setFailure({ key: requestKey, message: friendlyErrorMessage(err, 'Could not load the tax setup exceptions.') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, reason, requestKey]);

  const report = result?.key === requestKey ? result.report : null;
  const error = failure?.key === requestKey ? failure.message : null;
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  function choose(next: string | null) {
    setReason(next);
    setPage(1);
  }

  const totalPages = report ? Math.max(1, Math.ceil(report.totalCount / report.pageSize)) : 1;

  return (
    <div className="ts ts-ex" data-testid="tax-setup-exceptions">
      <h2>Tax setup exceptions</h2>
      <p className="ts-note">
        Active employees who have a tax setup item to look at. This is a to-do list: it does not block payroll, and an employee who is not listed is not
        necessarily ready, because EPN has not certified payroll tax calculation for any jurisdiction yet. Only employee numbers are shown.
      </p>

      {chips && (
        <div className="ts-ex__chips" role="group" aria-label="Filter by reason">
          <button type="button" className="ts-ex__chip" aria-pressed={reason === null} onClick={() => choose(null)}>
            All reasons
          </button>
          {chips.map((c) => (
            <button
              key={c.reason}
              type="button"
              className="ts-ex__chip"
              aria-pressed={reason === c.reason}
              disabled={c.count === 0 && reason !== c.reason}
              onClick={() => choose(c.reason)}
            >
              {reasonInfo(c.reason).label} <span className="ts-ex__count">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      {error && <RetryableError message={error} onRetry={retry} />}
      {!error && report === null && (
        <p className="ts-note" role="status">
          Loading…
        </p>
      )}
      {!error && report !== null && report.items.length === 0 && (
        <p className="ts-note" role="status" data-testid="exceptions-empty">
          {reason
            ? 'No employees have this reason.'
            : 'No active employee has a tax setup item to look at right now. This is not a statement that payroll tax calculation is certified.'}
        </p>
      )}
      {!error && report !== null && report.items.length > 0 && (
        <>
          <table className="ts-ex__table" aria-label="Employees with tax setup exceptions">
            <thead>
              <tr>
                <th scope="col">Employee number</th>
                <th scope="col">What needs attention</th>
              </tr>
            </thead>
            <tbody>
              {report.items.map((item) => (
                <tr key={item.employeeId} data-testid={`exception-row-${item.employeeNumber}`}>
                  <th scope="row">{item.employeeNumber}</th>
                  <td>
                    <ul className="ts-ex__reasons">
                      {item.reasons.map((r) => {
                        const info = reasonInfo(r);
                        const label = linkLabel(info.destination);
                        return (
                          <li key={r}>
                            <strong>{info.label}.</strong> {info.meaning} <span className="ts-note">{info.next}</span>{' '}
                            <Link to={destinationPath(item.employeeId, info.destination)} aria-label={`${label} for employee ${item.employeeNumber}: ${info.label}`}>
                              {label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <nav className="ts-ex__pager" aria-label="Exceptions pages">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
